import type { RecurrenceOverride } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";
import { ackedColumn, clampAck, type SyncDestination } from "./mut.js";

const OVERRIDE_CONTENT_SET = `recurrence_id=?,recurrence_id_ref=?,range=?,status=?,snoozed_until=?,due_at=?,duration_minutes=?,title=?,notes=?,priority=?,custom_color=?,all_day=?,rrule=?,updated_at=?,deleted_at=?`;
const OVERRIDE_UPDATE_SQL = `UPDATE recurrence_overrides SET ${OVERRIDE_CONTENT_SET},mut=mut+1 WHERE id=?`;
const OVERRIDE_UPDATE_SYNCED_SQL: Record<SyncDestination, string> = {
  server: `UPDATE recurrence_overrides SET ${OVERRIDE_CONTENT_SET},mut=mut+1,server_acked_mut=mut+1 WHERE id=?`,
  google: `UPDATE recurrence_overrides SET ${OVERRIDE_CONTENT_SET},mut=mut+1,google_acked_mut=mut+1 WHERE id=?`,
};

/** CRUD over the `recurrence_overrides` table. */
export class RecurrenceOverridesRepository {
  constructor(private readonly db: StorageAdapter) {}

  /** `synced` marks the row as already in step with that destination (an inbound
   *  sync/Google apply); a plain insert starts dirty for both. */
  async insert(o: RecurrenceOverride, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      `INSERT INTO recurrence_overrides (id,recurrence_id,recurrence_id_ref,range,status,snoozed_until,due_at,duration_minutes,title,notes,priority,custom_color,all_day,rrule,mut,server_acked_mut,google_acked_mut,created_at,updated_at,deleted_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
      [
        o.id, o.recurrence_id, o.recurrence_id_ref, o.range, o.status, o.snoozed_until,
        o.due_at, o.duration_minutes, o.title, o.notes, o.priority,
        o.custom_color, o.all_day === null ? null : o.all_day ? 1 : 0, o.rrule,
        1, synced === "server" ? 1 : 0, synced === "google" ? 1 : 0,
        o.created_at, o.updated_at, o.deleted_at,
      ],
    );
  }

  /** Every content write bumps `mut` (the sync engines' per-row dirty fact);
   *  `synced` self-acknowledges a write whose content came FROM that destination. */
  async update(o: RecurrenceOverride, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      synced ? OVERRIDE_UPDATE_SYNCED_SQL[synced] : OVERRIDE_UPDATE_SQL,
      [
        o.recurrence_id, o.recurrence_id_ref, o.range, o.status, o.snoozed_until, o.due_at,
        o.duration_minutes, o.title, o.notes, o.priority, o.custom_color,
        o.all_day === null ? null : o.all_day ? 1 : 0, o.rrule,
        o.updated_at, o.deleted_at, o.id,
      ],
    );
  }

  /** Clamped acknowledgement — see `TasksRepository.ackMut`. */
  async ackMut(id: string, dest: SyncDestination, upTo: number): Promise<void> {
    const current = await this.findById(id);
    if (!current) return;
    const next = clampAck(current, dest, upTo);
    if (next === null) return;
    await this.db.execute(`UPDATE recurrence_overrides SET ${ackedColumn(dest)}=? WHERE id=?`, [next, id]);
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM recurrence_overrides WHERE id=?`, [id]);
  }

  async listAllActive(): Promise<RecurrenceOverride[]> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM recurrence_overrides WHERE deleted_at IS NULL`,
    );
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync. */
  async listAll(): Promise<RecurrenceOverride[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM recurrence_overrides`);
    return rows.map(fromRow);
  }

  async findById(id: string): Promise<RecurrenceOverride | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM recurrence_overrides WHERE id=?`, [id],
    );
    return rows[0] ? fromRow(rows[0]) : null;
  }
}

function fromRow(r: Record<string, unknown>): RecurrenceOverride {
  return {
    id: String(r["id"]),
    recurrence_id: String(r["recurrence_id"]),
    recurrence_id_ref: String(r["recurrence_id_ref"]),
    range: r["range"] as RecurrenceOverride["range"],
    status: (r["status"] as RecurrenceOverride["status"] | null) ?? null,
    snoozed_until: (r["snoozed_until"] as string | null) ?? null,
    due_at: (r["due_at"] as string | null) ?? null,
    duration_minutes: r["duration_minutes"] == null ? null : Number(r["duration_minutes"]),
    title: (r["title"] as string | null) ?? null,
    notes: (r["notes"] as string | null) ?? null,
    priority: (r["priority"] as RecurrenceOverride["priority"]) ?? null,
    custom_color: (r["custom_color"] as string | null) ?? null,
    all_day: r["all_day"] == null ? null : Number(r["all_day"]) === 1,
    rrule: (r["rrule"] as string | null) ?? null,
    mut: Number(r["mut"] ?? 0),
    server_acked_mut: Number(r["server_acked_mut"] ?? 0),
    google_acked_mut: Number(r["google_acked_mut"] ?? 0),
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}
